import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction, useState, useEffect } from "react"
import { CustomerContractRatesType, Role, Search } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"
import { VehicleCard } from "../user/VehicleCard"
import Swal from "sweetalert2"
import { useMutation } from "react-query"
import { api } from "@/lib/api"
import {
  PencilSquareIcon,
  TrashIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline"
import { convertEquipmentType } from "@/lib/services"
import { Dropdown, Table } from "@/components"
import { SearchUser } from "@/components/general/Dropdown"

type Props = {
  popup: boolean
  role: Role
  salesRepId: string
  title: string
  icon: any
  slug: string
  setId: Dispatch<SetStateAction<string>>
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { contractRate: CustomerContractRatesType[] }
  header: {
    name: string
    width: string
  }[]
  handleForm: (form: CustomerContractRatesType) => void
  handleFormUpdate: (form: CustomerContractRatesType) => void
  handleFormDelete: (form: string) => void
  modalTitle: string
  setTitle: (title: string) => void
}

export const CustomerContractRates: FC<Props> = (props) => {
  const [user, setUser] = useState<SearchUser>()
  const [search, setSearch] = useState({
    input: "",
    userRole: props.role,
    salesRepID: props.salesRepId,
  })
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CustomerContractRatesType>()

  const mutation = useMutation((payload: Search) => {
    return api.AdminApi.Search(payload)
  })

  useEffect(() => {
    mutation.mutate({
      input: "",
      userRole: "CUSTOMER",
      salesRepID: "",
    })
  }, [])

  const onSubmit = (data: CustomerContractRatesType) => {
    if (data.id == undefined) {
      data.userID = user?.id as string
      props.handleForm(data)
    } else {
      data.customerId = user?.id as string
      props.handleFormUpdate(data)
    }
  }

  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      // color: "#ffffff",
      // background: "#333333",
      showCancelButton: true,
      confirmButtonColor: "#CEA16B",
      cancelButtonColor: "#a33333",
      customClass: {
        popup: 'custom-popup',
      },
      confirmButtonText: "<p style='color: black;'>Yes, Delete it!</p>",
    }).then(async (result) => {
      if (result.isConfirmed) {
        props.handleFormDelete(id)
        Swal.fire({
          icon: "success",
          title: "Record Deleted",
          // background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          }
        })
      }
    })
  }
  const populateData = (e: CustomerContractRatesType) => {
    props.setTitle('Update Customer Contract Rates')
    setValue("id", e.id)
    setValue("customerId", e.customerId)
    setValue("contractOrigin", e.contractOrigin)
    setValue("contractDestination", e.contractDestination)
    setValue("equipmentType", e.contractEquipmentType)
    setValue("ratePerMile", e.ratePerMile)
    setUser({
      id: e.customerId,
      firstName: e.customer.firstName,
      lastName: e.customer.lastName,
      email: e.customer.email,
    })

    props.setPopup(true)
  }

  useResetOnClose(props.popup, () => {
    reset({
      id: undefined,
      userID: undefined,
      equipmentType: "",
      contractOrigin: "",
      contractDestination: "",
      ratePerMile: undefined,
    })
    setUser({
      id: "",
      firstName: "",
      lastName: "",
      email: "",
    })
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="medium"
        title={props.modalTitle}
      >
        <div>
          <div className="grid md:grid-cols-10 grid-cols-12 gap-3">
            <fieldset className="md:col-span-2 col-span-4">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card"
                value="DRY_VAN"
                {...register("equipmentType", { required: true })}
              />
              <label htmlFor="vehicle_card">
                <VehicleCard image="/images/dry-van.svg" name="Dry Van" />
              </label>
            </fieldset>
            <fieldset className="md:col-span-2 col-span-4">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card1"
                value="FLATBED"
                {...register("equipmentType", { required: true })}
              />
              <label htmlFor="vehicle_card1">
                <VehicleCard image="/images/flatbed.svg" name="Flatbed" />
              </label>
            </fieldset>
            <fieldset className="md:col-span-2 col-span-4">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card2"
                value="REEFER"
                {...register("equipmentType", { required: true })}
              />
              <label htmlFor="vehicle_card2">
                <VehicleCard image="/images/reefer.svg" name="Reefer" />
              </label>
            </fieldset>
            <fieldset className="md:col-span-2 col-span-6">
              <input
                className="hidden vehicle_radio_customize "
                type="radio"
                id="vehicle_card3"
                value="STRAIGHT_TRUCK"
                {...register("equipmentType", { required: true })}
              />
              <label htmlFor="vehicle_card3">
                <VehicleCard
                  image="/images/straight-truck.svg"
                  name="Straight Truck"
                />
              </label>
            </fieldset>
            <fieldset className="md:col-span-2 col-span-6">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card4"
                value="SPRINTER_VAN"
                {...register("equipmentType", { required: true })}
              />
              <label htmlFor="vehicle_card4">
                <VehicleCard image="/images/sprinter.svg" name="Sprinter" />
              </label>
            </fieldset>
          </div>
          <div className="grid grid-cols-2 items-center gap-4 pt-3 pb-4">
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Contract Origin</label>
              <input
                className={
                  (errors.contractOrigin
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border-2 rounded placeholder:text-xs"
                }
                type="text"
                placeholder="Enter Contract Origin"
                {...register("contractOrigin", { required: true })}
              />
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">
                Contract Destination
              </label>
              <input
                className={
                  (errors.contractDestination
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border-2 rounded placeholder:text-xs"
                }
                type="text"
                placeholder="Enter Contract Destination"
                {...register("contractDestination", { required: true })}
              />
            </fieldset>
            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Add Customer</label>
              <Dropdown
                isCheck={true}
                search={search}
                handleSearch={(input) => mutation.mutate(input)}
                listingOfUsers={
                  mutation.data == undefined
                    ? null
                    : mutation.data.topMatchingUsers
                }
                selectedUser={user}
                setSelectedUser={setUser}
              />
            </fieldset>

            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Rate Per Mile</label>
              <input
                className={
                  (errors.ratePerMile ? "border-red-400" : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-transparent bg-white outline-none dark:text-white text-light-2 border-2 rounded placeholder:text-xs"
                }
                type="number"
                placeholder="Enter Rate Per Mile"
                {...register("ratePerMile", { required: true })}
              />
            </fieldset>
          </div>
          <button
            onClick={handleSubmit(onSubmit)}
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full dark:text-secondary-1 text-primary-1"
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.contractRate.map((e, i) => (
            <tr key={i} className="text-left">
              <td className={"whitespace-nowrap p-4 w-52"}>
                <p>
                  {e.customer.firstName} {e.customer.lastName}
                </p>
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {convertEquipmentType(e.contractEquipmentType) == "Van"
                  ? "Dry Van"
                  : convertEquipmentType(e.contractEquipmentType)}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {e.contractOrigin}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {e.contractDestination}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                <p>{e.ratePerMile}</p>
              </td>
              <td className={"whitespace-nowrap p-4 w-24"}>
                <div className="flex justify-end gap-2">
                  <PencilSquareIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => populateData(e)}
                  />
                  <TrashIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => onDelete(e.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
